/**
 * SessionHeader — one-line strip above the prescription list.
 *
 *   set 12 · 4.2 t · arms 60% / chest 40%
 *
 * Top two regions are picked by **tonnage** (not set count) — the brief
 * format mixes tonnage as the global metric ("4.2 t") and the percentages
 * stay in the same currency for consistency. Tonnage also better reflects
 * load: 4 sets of leg press at 200 kg is much more "leg" than 4 sets of
 * lateral raises at 8 kg, and that should show up in the balance.
 *
 * Region labels are lowercased to match the bilbo voice. Read-only —
 * no exclamation marks, no cheerleading.
 */

import type { RegionBalanceRow } from './types';

interface Props {
  setsCount: number;
  regionBalance: RegionBalanceRow[];
}

function formatTonnes(tonnageKg: number): string {
  if (tonnageKg < 1000) return `${tonnageKg} kg`;
  const t = tonnageKg / 1000;
  return `${t.toFixed(1)} t`;
}

export function SessionHeader({ setsCount, regionBalance }: Props) {
  const totalTonnage = regionBalance.reduce((acc, r) => acc + r.tonnageKg, 0);
  // Nothing useful to show before any tonnage has accrued. Avoids the
  // "set 2 · 0 kg" early-session noise that the freeform path hits before
  // the first weighted set is logged.
  if (totalTonnage <= 0 || setsCount === 0) return null;
  const top = [...regionBalance].sort((a, b) => b.tonnageKg - a.tonnageKg).slice(0, 2);

  return (
    <div className="session-header-strip" title={tooltipText(regionBalance, totalTonnage)}>
      <span className="session-header-pill">{formatTonnes(totalTonnage)}</span>
      {top.length > 0 && (
        <>
          <span className="session-header-sep">·</span>
          <span className="session-header-balance">
            {top.map((r, i) => {
              const pct = Math.round((r.tonnageKg / totalTonnage) * 100);
              return (
                <span key={r.region}>
                  {i > 0 && <span className="session-header-sep"> / </span>}
                  {r.region.toLowerCase()} {pct}%
                </span>
              );
            })}
          </span>
        </>
      )}
    </div>
  );
}

function tooltipText(regions: RegionBalanceRow[], total: number): string {
  if (total <= 0) return '';
  return [...regions]
    .sort((a, b) => b.tonnageKg - a.tonnageKg)
    .map((r) => `${r.region.toLowerCase()} ${Math.round((r.tonnageKg / total) * 100)}%`)
    .join(' · ');
}
